import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Divider,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import {
  getPlannerProfilePackagesDetails,
  getProviderProfileDetails,
  updatePackageCommissionForPlanner,
  updateProviderNeedTypes,
} from "../../../../../api/admin/provider-management";
import { routes } from "../../../../../constants/routes";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import ProfileHeader from "../../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { AlertComponent } from "../../../../../common/components";
import ContactLink from "../../../../../common/components/contact-link/contact-link";
import "./view-provider-profile-details.scss";
import { mapProviderTabsToData } from "./providerProfileMapper";
import { ViewServicePackageCardForPlanner } from "./view-service-package-details-for-planner";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  InsertDriveFileOutlined,
  ArrowDropDown,
} from "@mui/icons-material";
import ServicesAndMerchandise from "./service-and-merchandise";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import LinkAccountPopup from "./LinkAccountPopup";

const EMPTY_DOCUMENTS = {
  providerLicenceDocuments: [] as any[],
  afpDocuments: [] as any[],
  providerDocuments: [] as any[],
};

const ViewProviderProfileDetails = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [providerTabs, setProviderTabs] = useState<any>(null);
  const [error, setError] = useState<string>("-");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const { id } = useParams();
  const location = useLocation();
  const isPlannerSection = location.pathname.includes("planner-management");
  const [servicePackages, setServicePackages] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [addedNeedTypes, setAddedNeedTypes] = useState<string[]>([]);
  const [needTypeMenuAnchor, setNeedTypeMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [openLinkPopup, setOpenLinkPopup] = useState(false);
  const tabTitles = isPlannerSection
    ? [
        "Business Information",
        "Primary Information",
        "Location & Media Details",
        "Service Packages",
        "Documentation",
        "Bank Information",
      ]
    : [
        "Business Information",
        "Primary Information",
        "Location & Media Details",
        "Services & Merchandise",
        "Documentation",
        "Bank Information",
      ];
  const breadcrumbLinkForList = isPlannerSection
    ? [
        {
          label: "Planner Management",
          link: routes.PLANNER_PROFILES,
          stateData: { ...location.state },
        },
        {
          label: "Planner Profile",
          link: routes.PLANNER_PROFILES,
          stateData: { ...location.state },
        },
        { label: "Planner Details", link: "#", handleClick: () => {} },
      ]
    : [
        {
          label: "Provider Management",
          link: routes.PROVIDER_PROFILES,
          stateData: { ...location.state },
        },
        {
          label: "Provider Profile",
          link: routes.PROVIDER_PROFILES,
          stateData: { ...location.state },
        },
        { label: "Provider Details", link: "#", handleClick: () => {} },
      ];

  const [breadcrumbLink, setBreadcrumbLink] = useState(breadcrumbLinkForList);

  const fetchAdminProviderDetails = async () => {
    if (!id) return;
    setLoading(true);

    try {
      const response = await getProviderProfileDetails(Number(id));

      if (response?.status === 200 && response?.data?.success) {
        setProviderTabs(response.data.data.tabs);
        setPackages(response.data.data.tabs?.services?.data || []);
      } else {
        setError("Failed to fetch request details.");
      }
    } catch (error: any) {
      console.error("ERROR FETCHING PROVIDER DETAILS::", error);

      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while fetching data.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };
  const fetchAdminPlannerProfilePackageDetails = async () => {
    if (!id) return;
    setLoading(true);

    try {
      const response = await getPlannerProfilePackagesDetails(Number(id));

      if (response?.status === 200 && response?.data?.success) {
        const tabs = response.data.data.tabs;
        setProviderTabs(tabs);
        const packages = tabs?.packages?.data || [];
        const formattedPackages = packages.map((pkg: any) => ({
          id: pkg.id,
          name: pkg.packageName,
          price: Number(pkg.cost),
          commissionPercentage: pkg.commissionPercentage,
          commissionAmount: pkg.commissionFixed,
          description: pkg.description,
          isActive: pkg.isActive,

          services: (pkg.serviceCateogiresIncluded || []).map(
            (service: any) => ({
              name: service.categoryName,
              included: true,
            }),
          ),
        }));

        setServicePackages(formattedPackages);
      } else {
        setError("Failed to fetch request details.");
      }
    } catch (error: any) {
      console.error("ERROR FETCHING PLANNER PROFILE PACKAGE DETAILS::", error);

      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while fetching data.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };
  const UpdatePlannerProfilePackageDetails = async (
    packageId: string,
    type: "percentage" | "amount",
    value: number,
  ) => {
    if (!id || !packageId) return;

    setLoading(true);

    try {
      const body: any = {};

      if (type === "percentage") {
        body.commissionPercentage = value;
      } else {
        body.commissionFixed = value;
      }

      const response = await updatePackageCommissionForPlanner(
        id,
        packageId,
        body,
      );

      if (response?.status === 200 || response?.data?.success) {
        fetchAdminPlannerProfilePackageDetails();
      }
    } catch (error: any) {
      console.error("ERROR FETCHING PLANNER PROFILE PACKAGE DETAILS::", error);

      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while updating commission.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPlannerSection) {
      fetchAdminPlannerProfilePackageDetails();
    } else {
      fetchAdminProviderDetails();
    }
  }, [id]);

  const handleTabChange = (event: any, newValue: number) => {
    setActiveTab(newValue);
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const mappedTabData = useMemo(
    () => mapProviderTabsToData(providerTabs, isPlannerSection),
    [providerTabs, isPlannerSection],
  );

  const toCanonicalNeedType = (t: string): string => {
    const s = String(t).toLowerCase().trim().replace(/-/g, "_");
    if (s === "pre_need" || s === "preneed") return "preneed";
    if (s === "at_need" || s === "atneed") return "at_need";
    return s;
  };

  const toApiNeedType = (canonical: string): string => {
    const c = String(canonical).toLowerCase().trim();
    if (c === "preneed") return "pre_need";
    if (c === "at_need") return "at_need";
    return c;
  };
  const effectiveNeedTypes = useMemo(() => {
    const api =
      providerTabs?.businessDetails?.needTypes ??
      providerTabs?.businessInformation?.needTypes ??
      [];
    const fromApi = (Array.isArray(api) ? api : []).map((t: string) =>
      toCanonicalNeedType(t),
    );
    const combined = [...fromApi, ...addedNeedTypes];
    const seen = new Set<string>();
    return combined.filter((t) => {
      const c = toCanonicalNeedType(t);
      if (seen.has(c)) return false;
      seen.add(c);
      return true;
    });
  }, [
    providerTabs?.businessDetails?.needTypes,
    providerTabs?.businessInformation?.needTypes,
    addedNeedTypes,
  ]);

  useEffect(() => {
    setAddedNeedTypes([]);
  }, [id]);

  const documentsData = providerTabs?.documents ?? EMPTY_DOCUMENTS;
  const licenceDocs = documentsData?.providerLicenceDocuments ?? [];
  const providerDocs = documentsData?.providerDocuments ?? [];
  const pricingModelDocs = (() => {
    const biz = providerTabs?.businessInformation;
    const fromBiz =
      biz?.pricingModelDocuments ?? (biz as any)?.pricing_model_documents;
    if (Array.isArray(fromBiz)) return fromBiz;
    const docs = providerTabs?.documents;
    const providerDocsList =
      docs?.providerDocuments ?? (docs as any)?.provider_documents;
    if (Array.isArray(providerDocsList)) {
      return providerDocsList.filter(
        (d: any) =>
          d?.mediaSubType === "partner_package" ||
          (d as any)?.media_sub_type === "partner_package",
      );
    }
    return [];
  })();
  const formatDocDate = (iso: string) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "-";
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return "-";
    }
  };

  const renderTabContent = () => {
    const data = mappedTabData[activeTab] || [];
    const isServicesTab = activeTab === 3;
    const isDocumentationTab = activeTab === 4;
    const isLocationMediaTab = activeTab === 2;
    const locationMedia = providerTabs?.location_media_details;
    return (
      <Box mt={2}>
        {!isDocumentationTab && !isLocationMediaTab && (
          <Typography
            variant="h6"
            className="provider-profile__tab-title"
            sx={{ fontWeight: "400 !important", fontSize: "16px", mb: 2 }}
          >
            {tabTitles[activeTab]}
          </Typography>
        )}
        {isPlannerSection && isServicesTab && (
          <Box>
            {servicePackages.length > 0 ? (
              servicePackages.map((pkg) => (
                <ViewServicePackageCardForPlanner
                  key={pkg.id}
                  pkg={pkg}
                  onUpdateCommission={UpdatePlannerProfilePackageDetails}
                />
              ))
            ) : (
              <Typography
                sx={{
                  color: "#6b7280",
                  fontSize: "14px",
                  mt: 2,
                  textAlign: "center",
                }}
              >
                Planner has not added packages yet
              </Typography>
            )}
          </Box>
        )}
        {!isPlannerSection &&
          isServicesTab &&
          (packages.length > 0 ? (
            <ServicesAndMerchandise packages={packages} />
          ) : (
            <Typography
              sx={{
                color: "#6b7280",
                fontSize: "14px",
                mt: 2,
                textAlign: "center",
              }}
            >
              Provider has no services yet
            </Typography>
          ))}
        {isDocumentationTab && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "24px",
              alignItems:
                licenceDocs.length === 0 && providerDocs.length === 0
                  ? "stretch"
                  : "flex-start",
            }}
          >
            <Paper
              sx={{
                flex: "1 1 400px",
                maxWidth: 571.5,
                minWidth: 280,
                overflow: "hidden",
                borderRadius: "12px",
                border: "none",
                backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: "500 !important",
                    fontSize: "16px !important",
                    lineHeight: "100%",
                    letterSpacing: 0,
                    mb: 0.5,
                  }}
                >
                  Compliance
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: "400 !important",
                    fontSize: "14px !important",
                    lineHeight: "20px !important",
                    letterSpacing: 0,
                    color: "text.secondary",
                  }}
                >
                  Add compliance documents
                </Typography>
              </Box>
              <Box
                sx={{
                  minHeight: licenceDocs.length === 0 ? 184 : undefined,
                  borderRadius: "12px",
                  border: "1px solid #E0E0E0",
                  overflow: "hidden",
                  "& .MuiTable-root": {
                    borderCollapse: "separate",
                    borderSpacing: 0,
                  },
                  "& .MuiTableCell-root": { border: "none" },
                }}
              >
                <Table
                  size="medium"
                  sx={{
                    tableLayout: "fixed",
                    "& .MuiTableRow-root": {
                      "&:not(:last-child)": { borderBottom: "none" },
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "32%" }} />
                    <col style={{ width: "28%" }} />
                    <col style={{ width: "40%" }} />
                  </colgroup>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                      <TableCell
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "100%",
                          color: "grey.800",
                          py: 2,
                          px: 2.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        License Number
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "100%",
                          color: "grey.800",
                          py: 2,
                          px: 2.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Expiration Date
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "100%",
                          color: "grey.800",
                          py: 2,
                          px: 2.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Upload File
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {licenceDocs.length > 0 ? (
                      licenceDocs.map((lic: any) => (
                        <TableRow
                          key={lic?.id ?? lic?.licenceNumber}
                          sx={{ backgroundColor: "transparent" }}
                        >
                          <TableCell
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "20px",
                              color: "grey.800",
                              py: 1.5,
                              px: 2.5,
                              maxWidth: 140,
                            }}
                          >
                            <Tooltip
                              title={lic?.licenceNumber ?? "-"}
                              arrow
                              placement="top"
                            >
                              <span
                                style={{
                                  display: "block",
                                  maxWidth: "100%",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {lic?.licenceNumber ?? "-"}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "20px",
                              color: "grey.800",
                              py: 1.5,
                              px: 2.5,
                            }}
                          >
                            {formatDocDate(lic?.expiryDate)}
                          </TableCell>
                          <TableCell sx={{ py: 1.5, px: 2.5 }}>
                            {(() => {
                              const doc =
                                lic?.document?.[0] ?? lic?.documents?.[0];
                              const fileUrl = doc?.url;
                              if (fileUrl) {
                                return (
                                  <a
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#009689",
                                      fontSize: "14px",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    See Files
                                  </a>
                                );
                              }
                              return "-";
                            })()}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          sx={{
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "text.secondary",
                            textAlign: "center",
                            px: 2.5,
                            height: 184,
                            borderBottom: "none",
                          }}
                        >
                          No license documents
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
            <Paper
              sx={{
                flex: "1 1 400px",
                maxWidth: "100%",
                overflow: "hidden",
                borderRadius: "12px",
                border: "none",
                backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                  px: 2.5,
                  pt: 2,
                  pb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: "500 !important",
                    fontSize: "16px !important",
                    lineHeight: "100% !important",
                    mb: 0.5,
                  }}
                >
                  Documents
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: "400 !important",
                    fontSize: "14px !important",
                    lineHeight: "20px !important",
                    color: "text.secondary",
                  }}
                >
                  Add other documents
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  px: 3,
                  py: 2,
                  pr: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  opacity: 1,
                  minHeight: 0,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: "500 !important",
                    fontSize: "16px !important",
                    mb: 0.5,
                  }}
                >
                  {isPlannerSection
                    ? "Planner-Uploaded Documents"
                    : "Provider-Uploaded Documents"}
                </Typography>
                {providerDocs.length > 0 ? (
                  <Box
                    sx={{
                      borderRadius: "10px",
                      border: "1px solid #0000001A",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      p: 2,
                    }}
                  >
                    {providerDocs.map((doc: any) => (
                      <Paper
                        key={doc?.fileId ?? doc?.fileName}
                        variant="outlined"
                        sx={{
                          borderRadius: "10px",
                          border: "1px solid #0000001A",
                          backgroundColor: "#ECECF033",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            padding: "10px 14px 10px 14px",
                          }}
                        >
                          <InsertDriveFileOutlined
                            sx={{ color: "grey.500", fontSize: 32 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{ fontWeight: 500, fontSize: "14px" }}
                            >
                              {doc?.fileName ?? "Document"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Uploaded on {formatDocDate(doc?.uploadedAt)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              flexShrink: 0,
                            }}
                          >
                            <Chip
                              label="Uploaded"
                              size="small"
                              sx={{
                                backgroundColor: "#DCFCE7",
                                color: "#008236",
                                fontWeight: 500,
                              }}
                            />
                            <a
                              href={doc?.url || "#"}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#15585D",
                                fontSize: "14px",
                                whiteSpace: "nowrap",
                                textDecoration: "underline",
                                textUnderlineOffset: "4px",
                              }}
                            >
                              View
                            </a>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No provider-uploaded documents
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        )}
        {isLocationMediaTab && locationMedia && (
          <Box>
            {locationMedia.serviceableLocation?.length > 0 && (
              <Box
                mb={3}
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontStyle: "normal",
                  fontSize: "16px",
                  lineHeight: "24px",
                  letterSpacing: "-0.31px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: "400 !important",
                    fontStyle: "regular",
                    fontSize: "16px !important",
                    lineHeight: "24px !important",
                    letterSpacing: "-0.31px !important",
                    mb: 1.5,
                  }}
                >
                  All Serviceable Locations
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {locationMedia.serviceableLocation.map(
                    (loc: any, i: number) => (
                      <Paper
                        key={loc.id ?? i}
                        variant="outlined"
                        sx={{
                          p: 2,
                          pl: 3,
                          borderRadius: "8px",
                          borderTop: "1px solid #E5E7EB",
                          backgroundColor: "#F9FAFB",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 3,
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "-0.31px",
                            pl: 1,
                          }}
                        >
                          <Box
                            sx={{
                              minHeight: 56,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                display: "block",
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontStyle: "normal",
                                fontSize: "12px",
                                lineHeight: "18px",
                                letterSpacing: "0px",
                                color: "text.secondary",
                              }}
                            >
                              Zipcode
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Roboto",
                                fontWeight: "500 !important",
                                fontStyle: "normal",
                                fontSize: "14px !important",
                                lineHeight: "21px !important",
                                letterSpacing: "0px !important",
                              }}
                            >
                              {loc.zipcode ?? "-"}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              minHeight: 56,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                display: "block",
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontStyle: "normal",
                                fontSize: "12px",
                                lineHeight: "18px",
                                letterSpacing: "0px",
                                color: "text.secondary",
                              }}
                            >
                              City
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Roboto",
                                fontWeight: "500 !important",
                                fontStyle: "normal",
                                fontSize: "14px !important",
                                lineHeight: "21px !important",
                                letterSpacing: "0px !important",
                              }}
                            >
                              {loc.city ?? "-"}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              minHeight: 56,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                display: "block",
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontStyle: "normal",
                                fontSize: "12px",
                                lineHeight: "18px",
                                letterSpacing: "0px",
                                color: "text.secondary",
                              }}
                            >
                              State
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Roboto",
                                fontWeight: "500 !important",
                                fontStyle: "normal",
                                fontSize: "14px !important",
                                lineHeight: "21px !important",
                                letterSpacing: "0px !important",
                              }}
                            >
                              {loc.state ?? "-"}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ),
                  )}
                </Box>
              </Box>
            )}
            {locationMedia.providerMedia?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: "400 !important",
                    fontStyle: "regular",
                    fontSize: "16px !important",
                    lineHeight: "24px !important",
                    letterSpacing: "-0.31px !important",
                    mb: 1.5,
                  }}
                >
                  Provider Media
                </Typography>
                <Grid container spacing={2}>
                  {locationMedia.providerMedia.map((m: any, i: number) => (
                    <Grid item xs={12} sm={6} md={4} key={m.fileId ?? i}>
                      <Paper
                        variant="outlined"
                        sx={{
                          overflow: "hidden",
                          borderRadius: "8px",
                          border: "none",
                        }}
                      >
                        {m.mediaType === "image" && m.url ? (
                          <Box
                            component="a"
                            href={m.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: "block",
                              borderRadius: "8px",
                              overflow: "hidden",
                              bgcolor: "grey.100",
                            }}
                          >
                            <img
                              src={m.url}
                              alt={m.fileName ?? "Media"}
                              style={{
                                width: "100%",
                                aspectRatio: "4/3",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              aspectRatio: "4/3",
                              bgcolor: "grey.100",
                              borderRadius: "10px 10px 0 0",
                              overflow: "hidden",
                            }}
                          >
                            <InsertDriveFileOutlined
                              sx={{ color: "grey.500", fontSize: 48 }}
                            />
                          </Box>
                        )}
                        <Typography
                          sx={{ p: 1.5, fontWeight: 500, fontSize: "14px" }}
                          noWrap
                          title={m.fileName}
                        >
                          {m.fileName || "Media file"}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            {!locationMedia.serviceableLocation?.length &&
              !locationMedia.providerMedia?.length && (
                <Box
                  sx={{
                    mt: 4,
                    minHeight: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                    align="center"
                    sx={{ fontFamily: "Roboto", fontSize: "14px" }}
                  >
                    No location or media details
                  </Typography>
                </Box>
              )}
          </Box>
        )}
        {!isServicesTab && !isDocumentationTab && !isLocationMediaTab && (
          <Grid container spacing={activeTab === 5 ? 3 : 1.5}>
            {data.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  mb={activeTab === 5 ? 2.5 : 2}
                  sx={activeTab === 5 ? { textAlign: "left" } : undefined}
                >
                  {item.type === "needType" ? (
                    (() => {
                      const hasPreneed = effectiveNeedTypes.includes("preneed");
                      const hasAtNeed = effectiveNeedTypes.includes("at_need");
                      const remainingTypes: string[] = [];
                      if (!hasPreneed) remainingTypes.push("preneed");
                      if (!hasAtNeed) remainingTypes.push("at_need");
                      const displayLabel = (type: string) =>
                        type === "at_need" ? "At-Need" : "Preneed";
                      return (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.25,
                            verticalAlign: "middle",
                          }}
                        >
                          <Typography
                            className="provider-profile__label"
                            component="span"
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontStyle: "normal",
                              fontSize: "12px",
                              lineHeight: "16px",
                              letterSpacing: "0px",
                              color: "#6B7280",
                              display: "inline-block",
                            }}
                          >
                            Need Type
                          </Typography>
                          {!isPlannerSection && remainingTypes.length > 0 && (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) =>
                                  setNeedTypeMenuAnchor(e.currentTarget)
                                }
                                sx={{
                                  p: 0,
                                  minWidth: 0,
                                  width: 20,
                                  height: 20,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mt: "-2px",
                                }}
                                aria-label="Add need type"
                              >
                                <ArrowDropDown
                                  sx={{
                                    fontSize: "1.25rem",
                                    color: "#6B7280",
                                    lineHeight: 1,
                                  }}
                                />
                              </IconButton>
                              <Menu
                                anchorEl={needTypeMenuAnchor}
                                open={!!needTypeMenuAnchor}
                                onClose={() => setNeedTypeMenuAnchor(null)}
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "left",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                              >
                                {remainingTypes.map((type) => (
                                  <MenuItem
                                    key={type}
                                    onClick={async () => {
                                      const providerId = Number(id);
                                      if (
                                        !providerId ||
                                        Number.isNaN(providerId)
                                      )
                                        return;

                                      const nextAdded = [
                                        ...addedNeedTypes,
                                        type,
                                      ];
                                      const payload = [
                                        toApiNeedType(
                                          toCanonicalNeedType(type),
                                        ),
                                      ];

                                      try {
                                        setNeedTypeMenuAnchor(null);
                                        setAddedNeedTypes(nextAdded);

                                        await updateProviderNeedTypes(
                                          providerId,
                                          { needTypes: payload },
                                        );

                                        setAddedNeedTypes([]);
                                        await fetchAdminProviderDetails();

                                        setAlertMessage(
                                          "Need Type updated successfully",
                                        );
                                        setAlertType("success");
                                        setAlertOpen(true);
                                      } catch (e: any) {
                                        setAlertMessage(
                                          e?.response?.data?.message ||
                                            e?.message ||
                                            "Failed to update Need Type",
                                        );
                                        setAlertType("error");
                                        setAlertOpen(true);
                                        setNeedTypeMenuAnchor(null);
                                      }
                                    }}
                                  >
                                    {displayLabel(type)}
                                  </MenuItem>
                                ))}
                              </Menu>
                            </>
                          )}
                        </Box>
                      );
                    })()
                  ) : (
                    <Typography
                      className="provider-profile__label"
                      sx={
                        activeTab === 5
                          ? {
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontStyle: "normal",
                              fontSize: "12px",
                              lineHeight: "16px",
                              letterSpacing: "0px",
                              color: "#6B7280",
                            }
                          : undefined
                      }
                    >
                      {item.label}
                    </Typography>
                  )}
                  {item.label === "Pricing Model / Packages" ? (
                    <Box sx={{ mt: 1.25, maxWidth: 240 }}>
                      {pricingModelDocs.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {pricingModelDocs.map((doc: any) => {
                            const fileId = doc?.fileId ?? doc?.file_id;
                            const fileName =
                              doc?.fileName ?? doc?.file_name ?? "Document";
                            const uploadedAt =
                              doc?.uploadedAt ?? doc?.uploaded_at;
                            const url = doc?.url;
                            return (
                              <Box
                                key={fileId ?? fileName}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  py: 0.35,
                                  px: 0.75,
                                  borderRadius: "4px",
                                  border: "1px solid #E0E0E0",
                                  backgroundColor: "#F9FAFB",
                                }}
                              >
                                <InsertDriveFileOutlined
                                  sx={{ color: "grey.500", fontSize: 16 }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    sx={{
                                      fontFamily: "Roboto",
                                      fontWeight: 500,
                                      fontSize: "11px",
                                      color: "#212121",
                                      lineHeight: 1.2,
                                    }}
                                    noWrap
                                  >
                                    {fileName}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontSize: "10px", lineHeight: 1.2 }}
                                  >
                                    Uploaded on {formatDocDate(uploadedAt)}
                                  </Typography>
                                </Box>
                                {url && (
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#009689",
                                      fontSize: "11px",
                                      fontWeight: 500,
                                      textDecoration: "underline",
                                      textUnderlineOffset: "2px",
                                      flexShrink: 0,
                                    }}
                                  >
                                    View
                                  </a>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography
                          className="provider-profile__value"
                          sx={{
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#212121",
                          }}
                        >
                          -
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography
                      className="provider-profile__value"
                      sx={
                        activeTab === 5
                          ? {
                              fontFamily: "Roboto",
                              fontWeight: 500,
                              fontSize: "14px",
                              lineHeight: "20px",
                              letterSpacing: "0px",
                              color: "#212121",
                              mt: 0.5,
                            }
                          : undefined
                      }
                    >
                      {item.type === "phone" ? (
                        <ContactLink
                          type="Phone"
                          value={item.value || "-"}
                          className="provider-profile__value link-style"
                        />
                      ) : item.type === "email" ? (
                        <ContactLink
                          type="Email"
                          value={item.value || "-"}
                          className="provider-profile__value link-style"
                        />
                      ) : item.type === "website" ? (
                        <span className="provider-profile__value--website">
                          {item.value}
                        </span>
                      ) : item.type === "needType" ? (
                        (() => {
                          const raw = effectiveNeedTypes;
                          const displayLabel = (type: string) =>
                            type === "at_need" ? "At-Need" : "Preneed";
                          if (raw.length === 0) return "-";
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                flexWrap: "wrap",
                                mt: 0.5,
                              }}
                              className="provider-profile__need-type-wrapper"
                            >
                              {raw.map((type: string, i: number) => (
                                <span
                                  key={`${type}-${i}`}
                                  className="provider-profile__value--need-type"
                                >
                                  {displayLabel(type)}
                                </span>
                              ))}
                            </Box>
                          );
                        })()
                      ) : item.type === "documentLink" ? (
                        item.value && item.value !== "-" ? (
                          <a
                            href={item.value}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: item.linkText ? "#21a0a5" : "#009689",
                              textDecoration: "underline",
                            }}
                          >
                            {item.linkText ?? "View document"}
                          </a>
                        ) : (
                          "-"
                        )
                      ) : item.type === "linkAccount" ? (
                        <>
                          <span>- </span>
                          <span
                            onClick={() => setOpenLinkPopup(true)}
                            style={{
                              color: "#009689",
                              cursor: "pointer",
                              marginLeft: 6,
                              textDecoration: "underline",
                              fontWeight: 400,
                              fontSize: "14px",
                            }}
                          >
                            Link Partner
                          </span>
                        </>
                      ) : (
                        item.value
                      )}
                    </Typography>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <ProfileHeader breadcrumbLink={breadcrumbLink} />
      <Box className="provider-profile">
        <Typography
          variant="h4"
          className="provider-profile__heading"
          sx={{ fontWeight: 600, fontSize: "24px" }}
        >
          {providerTabs?.businessInformation?.businessName}
        </Typography>
        <Divider />

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 3 }}
          className="provider-profile__tabs"
        >
          <Tab label="Business Information" className="provider-profile__tab" />
          <Tab label="Primary Information" className="provider-profile__tab" />
          <Tab
            label="Location & Media Details"
            className="provider-profile__tab"
          />
          <Tab
            label={isPlannerSection ? "Service Packages" : "Services"}
            className="provider-profile__tab"
          />
          <Tab label="Documentation" className="provider-profile__tab" />
          <Tab label="Bank Details" className="provider-profile__tab" />
        </Tabs>

        <Box className="provider-profile__tab-content">
          {renderTabContent()}
        </Box>
      </Box>
      <AlertComponent
        open={alertOpen}
        message={alertMessage || ""}
        type={alertType}
        onClose={() => setAlertOpen(false)}
      />
      <LinkAccountPopup
        open={openLinkPopup}
        onClose={() => setOpenLinkPopup(false)}
        sourceId={id}
        onSuccess={() => {
          setAlertMessage(`Partner linked successfully`);
          setAlertType("success");
          setAlertOpen(true);
          fetchAdminProviderDetails()
        }}
        onError={(msg) => {
          setAlertMessage(msg);
          setAlertType("error");
          setAlertOpen(true);
        }}
      />
    </Box>
  );
};

export default ViewProviderProfileDetails;
